import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

// Safe column selections — never SELECT *, never expose internal costs/margins
const SAFE_PROJECT_COLS = "id, name, project_number, status, address, city, state, start_date, scheduled_completion, location, description" as const;
const SAFE_INVOICE_COLS = "id, invoice_number, invoice_date, due_date, total, amount_paid, status, sent_at, paid_at, payment_method, payment_reference, customer_visible" as const;
const SAFE_CO_COLS = "id, number, description, amount, status, created_at, customer_visible" as const;
const SAFE_APPROVAL_COLS = "id, request_type, title, description, status, urgency, created_at, resolved_at, resolution_note, customer_visible, projects(name)" as const;

export interface CustomerProject {
  id: string;
  name: string;
  project_number: string;
  status: string;
  address: string | null;
  city: string | null;
  state: string | null;
  start_date: string | null;
  scheduled_completion: string | null;
  location: string | null;
  description: string | null;
}

export interface CustomerInvoice {
  id: string;
  invoice_number: string;
  invoice_date: string;
  due_date: string | null;
  total: number;
  amount_paid: number;
  status: string;
  sent_at: string | null;
  paid_at: string | null;
  payment_method: string | null;
  payment_reference: string | null;
}

export interface CustomerChangeOrder {
  id: string;
  number: string;
  description: string | null;
  amount: number;
  status: string;
  created_at: string;
}

export interface CustomerApproval {
  id: string;
  request_type: string;
  title: string;
  description: string | null;
  status: string;
  urgency: string;
  created_at: string;
  resolved_at: string | null;
  resolution_note: string | null;
  projects?: { name: string } | null;
}

export interface CustomerDocument {
  id: string;
  title: string;
  description: string | null;
  file_path: string;
  file_name: string;
  file_size: number | null;
  created_at: string;
  project_id: string | null;
}

// ─── Projects ───
export const useCustomerProjects = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["customer-projects", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select(SAFE_PROJECT_COLS)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as CustomerProject[];
    },
    enabled: !!user,
  });
};

// ─── Single project ───
export const useCustomerProjectDetail = (projectId: string | undefined) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["customer-project-detail", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select(SAFE_PROJECT_COLS)
        .eq("id", projectId!)
        .single();
      if (error) throw error;
      return data as CustomerProject;
    },
    enabled: !!user && !!projectId,
  });
};

// ─── Invoices ───
export const useCustomerInvoices = (projectId?: string) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["customer-invoices", user?.id, projectId],
    queryFn: async () => {
      let query = supabase
        .from("invoices")
        .select(SAFE_INVOICE_COLS)
        .eq("customer_visible", true)
        .order("invoice_date", { ascending: false });
      if (projectId) query = query.eq("project_id", projectId);
      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as CustomerInvoice[];
    },
    enabled: !!user,
  });
};

// ─── Change Orders ───
export const useCustomerChangeOrders = (projectId?: string) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["customer-change-orders", user?.id, projectId],
    queryFn: async () => {
      let query = supabase
        .from("change_orders")
        .select(SAFE_CO_COLS)
        .eq("customer_visible", true)
        .order("created_at", { ascending: false });
      if (projectId) query = query.eq("project_id", projectId);
      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as CustomerChangeOrder[];
    },
    enabled: !!user,
  });
};

// ─── Approvals ───
export const useCustomerApprovals = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["customer-approvals", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("approval_requests")
        .select(SAFE_APPROVAL_COLS)
        .eq("customer_visible", true)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as CustomerApproval[];
    },
    enabled: !!user,
  });
};

// ─── Documents ───
export const useCustomerDocuments = (projectId?: string) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["customer-documents", user?.id, projectId],
    queryFn: async () => {
      let query = supabase
        .from("customer_documents")
        .select("id, title, description, file_path, file_name, file_size, created_at, project_id")
        .order("created_at", { ascending: false });
      if (projectId) query = query.eq("project_id", projectId);
      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as CustomerDocument[];
    },
    enabled: !!user,
  });
};

// ─── Dashboard metrics ───
export const useCustomerDashboard = () => {
  const projects = useCustomerProjects();
  const invoices = useCustomerInvoices();
  const changeOrders = useCustomerChangeOrders();
  const approvals = useCustomerApprovals();

  const activeProjects = (projects.data ?? []).filter((p) => p.status === "active").length;
  const totalProjects = (projects.data ?? []).length;
  const openInvoices = (invoices.data ?? []).filter((i) => ["approved", "sent", "overdue"].includes(i.status));
  const openInvoiceTotal = openInvoices.reduce((sum, i) => sum + i.total, 0);
  const pendingCOs = (changeOrders.data ?? []).filter((co) => co.status === "submitted").length;
  const pendingApprovals = (approvals.data ?? []).filter((a) => a.status === "pending").length;

  return {
    activeProjects,
    totalProjects,
    openInvoiceCount: openInvoices.length,
    openInvoiceTotal,
    pendingCOs,
    pendingApprovals,
    isLoading: projects.isLoading || invoices.isLoading,
    recentInvoices: (invoices.data ?? []).slice(0, 5),
    recentProjects: (projects.data ?? []).slice(0, 5),
  };
};
